<template>
  <b-form @submit.prevent="onSubmit">
    <b-modal
      id="submission-form--error-modal"
      title="Error Submitting Order!"
      hide-footer
    >
      <div class="d-block">
        {{ submitError }}
      </div>
      <b-button
        class="mt-3"
        variant="danger"
        block
        @click="$bvModal.hide('submission-form--error-modal')"
      >
        OK
      </b-button>
    </b-modal>
    <div v-if="formState === 'user_input'">

      <h2>Contact Information</h2>
      <p>
        Please enter your contact information and upload your image below. We will contact you to
        determine the quantity and type of prints.
      </p>
      <p>
        Our <a href="https://www.sketchcalgary.ca/art-print-services/">Art Print Services page</a>
        describes our process, equipment, and pricing.
      </p>
      <b-form-group
        label="First Name"
        label-for="contact-form--first-name"
      >
        <b-form-input
          id="contact-form--first-name"
          type="text"
          required
          autofocus
          v-model="firstName"
        />
      </b-form-group>

      <b-form-group
        label="Last Name"
        label-for="contact-form--last-name"
      >
        <b-form-input
          id="contact-form--last-name"
          type="text"
          required
          v-model="lastName"
        />
      </b-form-group>

      <b-form-group
        label="E-Mail"
        label-for="contact-form--e-mail"
        description="Please enter your e-mail in the form of 'myname@example.net'."
      >
        <b-form-input
          id="contact-form--e-mail"
          type="email"
          required
          v-model="email"
        />
      </b-form-group>

      <b-form-group
        label="Phone Number"
        label-for="contact-form--telephone"
        description="Please enter your number with area code."
      >
        <b-form-input
          id="contact-form--telephone"
          type="tel"
          required
          v-model="phoneNumber"
        />
      </b-form-group>

      <b-form-group
        label="How would you like us to contact you?"
        label-for="contact-form--contact-method"
        description="We will contact you to determine the type and quantity of prints you require."
      >
        <b-form-select
          v-model="preferredContactMethod"
          :options="contactOptions"
        />
      </b-form-group>

      <b-form-group
        label="How did you hear about us?"
        label-for="contact-form--referral"
      >
        <b-form-input
          id="contact-form--referral"
          type="tel"
          v-model="referral"
        />
      </b-form-group>

      <h2>Image</h2>
      <b-form-group
        label="Print File"
        label-for="contact-form--print-file"
        description="Please upload a JPEG, PNG, or TIFF"
      >
        <b-form-file
          v-model="file"
          :state="Boolean(file)"
          placeholder="Choose a file or drop it here..."
          accept="image/jpeg, image/tiff, image/png"
          drop-placeholder="Drop file here..."
        />
        <div class="mt3">
          Selected file: {{ file ? file.name : '' }}
        </div>
      </b-form-group>

      <b-form-group
        label="Job Details"
        label-for="contact-form--details"
        description="Please include the following details for your project: print size, quantity of
         prints, paper type, any special instructions."
      >
        <b-form-textarea
          id="contact-form--details"
          type="text"
          required
          v-model="details"
          rows="3"
        />
      </b-form-group>

      <h2>Disclaimer</h2>
      <p>
        Your personal information will only be used to
        contact you in the event we need to ask questions or inform you of the status of your
        order.
        Your contact information will not be sold to a third party. Submitting this form will not
        add you to any mailing or call lists. How we protect your privacy is detailed in our
        <b-link to="/policy/privacy" target="_blank">Privacy Policy</b-link>.
      </p>
      <p>
        We are not responsible for archiving submitted images. Please maintain your own backups
        in the event you need prints in the future.
      </p>
      <p>
        Submitting this form is not a commitment to a purchase.
      </p>

      <h2>Agreement</h2>
      <p>
        By submitting this form you agree to our
        <b-link to="/policy/terms-of-use" target="_blank">Terms of Use</b-link> and
        <b-link to="/policy/acceptable-use" target="_blank">Acceptable Use Policy</b-link>.
      </p>

      <b-button type="submit" variant="primary">Submit</b-button>
    </div>
    <div v-else-if="formState === 'submitting'">
      <b-progress :value="submitCurrent" :max="submitTotal" animated />
    </div>
    <div v-else>
      <div class="d-block">
        <h3>Success!</h3>
        <div>
          Your image has been uploaded and we have been notified.  We will contact you
          when you are next in queue.
        </div>
      </div>
      <b-button
        @click="onRefresh"
        variant="success"
        class="d-block"
      >
        Submit another image
      </b-button>
    </div>

  </b-form>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import Order, { ContactMethod } from '@/models/Order';

interface ContactOption {
  value: ContactMethod;
  text: string;
}

type ContactOptions = Array<ContactOption>;

type FormState = 'user_input'|'submitting'|'done';

interface Modal {
  show: () => void;
}

@Component
export default class ContactForm extends Vue {
  selectedContact = 'email';

  preferredContactMethod: ContactMethod;

  phoneNumber: string;

  email: string;

  lastName: string;

  firstName: string;

  referral: string;

  file: File|null = null;

  details: string;

  formState: FormState;

  submitError: string;

  submitCurrent:number;

  submitTotal: number;

  contactOptions: ContactOptions;

  constructor() {
    super();
    this.selectedContact = 'email';
    this.file = null;
    this.formState = 'user_input';
    this.submitError = '';
    this.submitCurrent = 0;
    this.submitTotal = 100;
    this.contactOptions = [
      { value: 'email', text: 'Please e-mail me.' },
      { value: 'call', text: 'Please call my phone number.' },
      { value: 'text', text: 'Please text my phone number.' },
    ];
    this.preferredContactMethod = 'email';
    this.phoneNumber = '';
    this.email = '';
    this.lastName = '';
    this.firstName = '';
    this.referral = '';
    this.details = '';
    this.setDefault();
  }

  protected setDefault(): void {
    // Set the properties pertaining to individual jobs back to default.
    // Leave contact information and other details in place so a user can easily submit
    // multiple jobs.
    this.file = null;
    this.formState = 'user_input';
    this.submitError = '';
    this.submitCurrent = 0;
    this.submitTotal = 100;
    this.contactOptions = [
      { value: 'email', text: 'Please e-mail me.' },
      { value: 'call', text: 'Please call my phone number.' },
      { value: 'text', text: 'Please text my phone number.' },
    ];
    this.details = '';
  }

  async onSubmit(): Promise<void> {
    if (this.file === null) {
      return;
    }
    this.formState = 'submitting';
    const order: Order = {
      preferredContactMethod: this.preferredContactMethod,
      phoneNumber: this.phoneNumber,
      email: this.email,
      lastName: this.lastName,
      firstName: this.firstName,
      referral: this.referral,
      details: this.details,
    };
    try {
      console.log('getting auth key:');
      const key = await this.$authorization.executeAction('CreateOrder');
      console.log('creating order:', key);
      await this.$gateway.orderCreate(key, order, this.file, (current: number, total: number) => {
        this.submitCurrent = current;
        this.submitTotal = total;
      });
      this.formState = 'done';
    } catch (e) {
      console.log('Got Error:', e);
      this.formState = 'user_input';
      this.submitError = e.message;
      this.$bvModal.show('submission-form--error-modal');
    }
  }

  onRefresh(): void {
    this.setDefault();
  }
}
</script>

<style scoped>

</style>
